import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { useDocuments } from '../../context/DocumentContext';
import { documents as documentsApi } from '../../utils/api';
import CoinSelector from '../shared/CoinSelector';

const DocumentUploadForm = ({ open, onClose, onSuccess, preSelectedCoins }) => {
  const navigate = useNavigate();
  const { uploadDocument } = useDocuments();
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    typeId: '',
    notes: '',
    coinIds: [],
    file: null
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDocumentTypes = async () => {
      try {
        const types = await documentsApi.getTypes();
        setDocumentTypes(types);
      } catch (error) {
        console.error('Failed to fetch document types:', error);
      }
    };

    if (open) {
      fetchDocumentTypes();
      // Set pre-selected coins if provided
      if (preSelectedCoins?.size > 0) {
        setFormData(prev => ({
          ...prev,
          coinIds: Array.from(preSelectedCoins)
        }));
      }
    } else {
      // Reset form when modal closes
      setFormData({
        title: '',
        typeId: '',
        notes: '',
        coinIds: [],
        file: null
      });
      setError(null);
    }
  }, [open, preSelectedCoins]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check if file is PDF or image
      const isPDF = file.type === 'application/pdf';
      const isImage = file.type.startsWith('image/');

      if (!isPDF && !isImage) {
        setError('Only PDF and image files are allowed');
        e.target.value = ''; // Clear the file input
        return;
      }

      setError(null); // Clear any previous errors
      setFormData(prev => ({
        ...prev,
        file
      }));
    }
  };

  const handleCoinSelection = (selectedIds) => {
    setFormData(prev => ({
      ...prev,
      coinIds: selectedIds
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file) {
      setError('Please select a file to upload');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('typeId', formData.typeId);
      submitData.append('notes', formData.notes);
      submitData.append('coinIds', JSON.stringify(formData.coinIds));
      submitData.append('file', formData.file);

      const document = await uploadDocument(submitData);
      if (onSuccess) {
        onSuccess(document);
      } else {
        navigate(`/documents/${document.id}`);
      }
      onClose?.();
    } catch (err) {
      setError(err.message || 'Failed to upload document');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition.Root show={open} as={React.Fragment}>
      <Dialog 
        as="div" 
        className="relative z-[1100]" 
        onClose={onClose}
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-3 pt-4 text-left shadow-xl transition-all sm:my-6 sm:w-full sm:max-w-3xl sm:p-5">
                <form onSubmit={handleSubmit}>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                        Upload Document
                      </Dialog.Title>
                      <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <span className="sr-only">Close</span>
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {error && (
                      <div className="rounded-md bg-red-50 p-4">
                        <p className="text-sm text-red-700">{error}</p>
                      </div>
                    )}

                    <div>
                      <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                        Title
                      </label>
                      <input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="typeId" className="block text-sm font-medium text-gray-700">
                        Document Type
                      </label>
                      <select
                        id="typeId"
                        name="typeId"
                        required
                        value={formData.typeId}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="">Select a type</option>
                        {documentTypes.map(type => (
                          <option key={type.id} value={type.id.toString()}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="file" className="block text-sm font-medium text-gray-700">
                        File
                      </label>
                      <input
                        type="file"
                        id="file"
                        accept=".pdf,image/*"
                        onChange={handleFileChange}
                        className="mt-1 block w-full text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-full file:border-0
                          file:text-sm file:font-semibold
                          file:bg-indigo-50 file:text-indigo-700
                          hover:file:bg-indigo-100"
                      />
                    </div>

                    {!preSelectedCoins && (
                      <fieldset>
                        <legend className="block text-sm font-medium text-gray-700">
                          Related Coins
                        </legend>
                        <CoinSelector
                          selectedCoinIds={formData.coinIds}
                          onChange={handleCoinSelection}
                          className="mt-1"
                        />
                      </fieldset>
                    )}

                    {preSelectedCoins?.size > 0 && (
                      <fieldset>
                        <legend className="block text-sm font-medium text-gray-700">
                          Related Coins
                        </legend>
                        <div className="mt-1 text-sm text-gray-500">
                          {preSelectedCoins.size} coin{preSelectedCoins.size !== 1 ? 's' : ''} selected
                        </div>
                      </fieldset>
                    )}

                    <div>
                      <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                        Notes
                      </label>
                      <textarea
                        id="notes"
                        name="notes"
                        rows={2}
                        value={formData.notes}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  <div className="mt-4 sm:mt-4 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      ) : (
                        'Upload'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default DocumentUploadForm;
